import { catchUpSpan, isProjectSelectable, wallMinOf } from '@wiztime/core';
import { useTranslation } from 'react-i18next';
import { useToday } from '../../../hooks/useRouteView';
import { formatRange } from '../../../lib/format';
import { useDayData } from '../DayDataContext';
import { PickerKind, useDayUi } from '../dayUiStore';
import { useDayCommands } from '../useDayCommands';
import styles from './Cards.module.css';

/**
 * Phones only: a 44px button "Catch up 14:00-16:30" above the timeline. The span runs from the
 * exact end of the latest block (or the start of the free stretch that contains now, off-grid
 * minutes kept, 2026-10b §3.3) to now snapped down to the time step, exists only when it is
 * entirely empty and at least the minimum duration long. A press opens the project picker for it
 * (or, in quick-mark mode, records the quick-mark project); the system never fills it by itself.
 */
export function CatchUpButton() {
  const { t } = useTranslation();
  const { date, settings, blocks, blocksLoading, blocksError, projects, now } = useDayData();
  const commands = useDayCommands();
  const today = useToday();
  const quickMarkId = useDayUi((state) => state.quickMark.projectId);
  if (date !== today || blocksLoading || blocksError !== null) return null;

  const span = catchUpSpan(
    wallMinOf(now, date),
    { step: settings.timeStepMin, minDuration: settings.minDurationMin, dayStartMin: settings.dayStartMin },
    blocks.map((block) => block.data),
  );
  if (span === null) return null;

  const quickProject = quickMarkId === null ? undefined : projects.byId.get(quickMarkId);
  const range = formatRange(span.startMin, span.endMin);
  return (
    <button
      type="button"
      className={styles.catchUp}
      data-testid="catch-up"
      data-start={span.startMin}
      data-end={span.endMin}
      onClick={(event) => {
        if (quickProject !== undefined && isProjectSelectable(quickProject, projects.byId)) {
          // A quick-mark creation here consumes the "once" like any other mark (§4.3); the
          // state is captured at command start, so a late outcome never settles a newer state.
          const startedFrom = useDayUi.getState().quickMark;
          void commands.createBlock(date, span, quickProject.id).then((outcome) => {
            useDayUi.getState().quickMarkSettle(outcome, startedFrom);
          });
          return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        useDayUi.getState().openPicker({
          kind: PickerKind.Create,
          date,
          span,
          anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
        });
      }}
    >
      {t('catchUp.button', { range })}
    </button>
  );
}
